/**
 * SignaturePad — small canvas signature capture.
 * Returns a JPEG data URL on save via `onSave`.
 */
import { useEffect, useRef, useState } from "react";
import { X, Check, Eraser, PenLine } from "lucide-react";

const SignaturePad = ({ open, onClose, onSave }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    if (!open) return;
    const c = canvasRef.current;
    const w = wrapRef.current;
    if (!c || !w) return;
    c.width = w.clientWidth;
    c.height = 240;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    setEmpty(true);
  }, [open]);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
  };

  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) setEmpty(false);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
    setEmpty(true);
  };

  const save = () => {
    if (empty) return;
    const data = canvasRef.current.toDataURL("image/jpeg", 0.85);
    onSave(data);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="signature-overlay">
      <div className="bg-white border-2 border-slate-900 w-full max-w-lg" onClick={(e)=>e.stopPropagation()} data-testid="signature-pad">
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#1E1B4B] text-white flex items-center justify-center"><PenLine size={18} strokeWidth={2.5}/></div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Sign &amp; complete</p>
              <h3 className="font-black text-slate-900">Inspector signature</h3>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 border-2 border-slate-300 flex items-center justify-center hover:bg-slate-100" data-testid="signature-close"><X size={18}/></button>
        </div>
        <div className="p-4">
          <div ref={wrapRef} className="bg-slate-50 border-2 border-dashed border-slate-300">
            <canvas
              ref={canvasRef}
              data-testid="signature-canvas"
              className="block w-full touch-none cursor-crosshair"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">Use your finger (touch) or mouse to sign above.</p>
          <div className="mt-4 flex gap-2">
            <button onClick={save} disabled={empty} data-testid="signature-save"
              className="flex-1 h-12 inline-flex items-center justify-center gap-2 bg-[#1E1B4B] text-white font-bold border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-40">
              <Check size={16} strokeWidth={2.5}/>Sign &amp; lock inspection
            </button>
            <button onClick={clear} disabled={empty} data-testid="signature-clear"
              className="h-12 px-4 bg-white border-2 border-slate-300 text-slate-700 font-bold hover:border-slate-900 disabled:opacity-40">
              <Eraser size={16} strokeWidth={2.5}/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignaturePad;
